import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, History, Loader2, MessageSquare, Reply } from "lucide-react";

import {
  createGrantOpportunityComment,
  listGrantOpportunityComments,
  type GrantOpportunityComment,
} from "@/api/grant_opportunity_comments";
import {
  listGrantOpportunityEvents,
  type GrantOpportunityEvent,
} from "@/api/grant_opportunity_events";
import {
  GRANT_OPPORTUNITY_DOCUMENT_ROLE_LABELS,
  listGrantOpportunityDocuments,
  type GrantOpportunityDocumentLink,
} from "@/api/grant_opportunity_documents";
import {
  listGrantOpportunityLinkedDocumentComments,
  type GrantOpportunityLinkedDocumentComment,
} from "@/api/grant_opportunity_linked_document_comments";
import { groupComments } from "@/components/comments/groupComments";
import { groupGrantOpportunityComments } from "@/components/grant_suite/GrantOpportunityComments";
import { formatGrantOpportunityEventSummary } from "@/components/grant_suite/grantSuiteDisplay";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import {
  grantOpportunityEventsQueryKey,
  invalidateGrantSuitePipeline,
} from "@/lib/grantSuiteQueryKeys";
import { useCurrentUser } from "@/context/UserProvider";
import { userIsOutsideUser } from "@/lib/grantSuiteAccess";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

type GrantOpportunityActivityProps = {
  municipalityId: number;
  opportunityId: number;
  fileViewerEnabled: boolean;
};

type ActivityEntry =
  | {
    kind: "comment";
    at: string;
    comment: GrantOpportunityComment;
    replies: GrantOpportunityComment[];
  }
  | {
      kind: "document";
      at: string;
      link: GrantOpportunityDocumentLink;
    }
  | {
      kind: "event";
      at: string;
      event: GrantOpportunityEvent;
    }
  | {
      kind: "document_comment";
      at: string;
      comment: GrantOpportunityLinkedDocumentComment;
      replies: GrantOpportunityLinkedDocumentComment[];
    };

export function buildGrantOpportunityActivity(
  comments: GrantOpportunityComment[],
  documents: GrantOpportunityDocumentLink[],
  events: GrantOpportunityEvent[] = [],
  documentComments: GrantOpportunityLinkedDocumentComment[] = [],
): ActivityEntry[] {
  const { topLevel, repliesByParent } = groupGrantOpportunityComments(comments);
  const {
    topLevel: documentTopLevel,
    repliesByParent: documentRepliesByParent,
  } = groupComments(documentComments);

  const entries: ActivityEntry[] = [
    ...topLevel.map((comment) => ({
      kind: "comment" as const,
      at: comment.created_at,
      comment,
      replies: repliesByParent.get(comment.id) ?? [],
    })),
    ...documentTopLevel.map((comment) => ({
      kind: "document_comment" as const,
      at: comment.created_at,
      comment,
      replies: documentRepliesByParent.get(comment.id) ?? [],
    })),
    ...documents.map((link) => ({
      kind: "document" as const,
      at: link.created_at,
      link,
    })),
    ...events.map((event) => ({
      kind: "event" as const,
      at: event.created_at,
      event,
    })),
  ];

  // Chronological, earliest at the top — new entries append next to the
  // composer at the bottom instead of jumping to the top (ENG-1722).
  return entries.sort(
    (left, right) => new Date(left.at).getTime() - new Date(right.at).getTime(),
  );
}

export function GrantOpportunityActivity({
  municipalityId,
  opportunityId,
  fileViewerEnabled,
}: GrantOpportunityActivityProps) {
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  // Outside collaborators read the timeline but can't post (ENG-1877): the
  // composer and reply affordances go away rather than 403 on submit.
  const outsideUser = userIsOutsideUser(currentUser);
  const [draft, setDraft] = useState("");
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyDraft, setReplyDraft] = useState("");

  const commentsQueryKey = ["grant_opportunity_comments", municipalityId, opportunityId] as const;
  const documentsQueryKey = ["grant_opportunity_documents", municipalityId, opportunityId] as const;
  const eventsQueryKey = grantOpportunityEventsQueryKey(municipalityId, opportunityId);
  const linkedDocumentCommentsQueryKey = [
    "grant_opportunity_linked_document_comments",
    municipalityId,
    opportunityId,
  ] as const;

  const { data: comments = [], isLoading: commentsLoading, error: commentsError } = useQuery({
    queryKey: commentsQueryKey,
    queryFn: () => listGrantOpportunityComments(municipalityId, opportunityId),
  });

  const {
    data: documents = [],
    isLoading: documentsLoading,
    error: documentsError,
  } = useQuery({
    queryKey: documentsQueryKey,
    queryFn: () => listGrantOpportunityDocuments(municipalityId, opportunityId),
    enabled: fileViewerEnabled,
  });

  const { data: events = [], isLoading: eventsLoading, error: eventsError } = useQuery({
    queryKey: eventsQueryKey,
    queryFn: () => listGrantOpportunityEvents(municipalityId, opportunityId),
  });

  const {
    data: linkedDocumentComments = [],
    isLoading: linkedDocumentCommentsLoading,
    error: linkedDocumentCommentsError,
  } = useQuery({
    queryKey: linkedDocumentCommentsQueryKey,
    queryFn: () => listGrantOpportunityLinkedDocumentComments(municipalityId, opportunityId),
    enabled: fileViewerEnabled,
  });

  const activity = useMemo(
    () =>
      buildGrantOpportunityActivity(
        comments,
        fileViewerEnabled ? documents : [],
        events,
        fileViewerEnabled ? linkedDocumentComments : [],
      ),
    [comments, documents, events, linkedDocumentComments, fileViewerEnabled],
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: commentsQueryKey });
    queryClient.invalidateQueries({ queryKey: documentsQueryKey });
    queryClient.invalidateQueries({ queryKey: eventsQueryKey });
    queryClient.invalidateQueries({ queryKey: linkedDocumentCommentsQueryKey });
    invalidateGrantSuitePipeline(queryClient, municipalityId);
  };

  const createMutation = useMutation({
    mutationFn: ({ body, parentId }: { body: string; parentId?: number | null }) =>
      createGrantOpportunityComment(municipalityId, opportunityId, body, parentId),
    onSuccess: () => {
      setDraft("");
      setReplyDraft("");
      setReplyingTo(null);
      invalidate();
    },
  });

  const loading =
    commentsLoading ||
    eventsLoading ||
    (fileViewerEnabled && (documentsLoading || linkedDocumentCommentsLoading));

  const feedError =
    commentsError ||
    eventsError ||
    (fileViewerEnabled && (documentsError || linkedDocumentCommentsError));

  return (
    <div className="space-y-4" data-testid="grant-opportunity-activity">
      <p className="text-sm text-muted-foreground">
        Comments, file notes, status changes, milestones, and linked files in one timeline.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading activity…
        </div>
      ) : feedError ? (
        <p className="text-sm text-destructive">Could not load activity.</p>
      ) : activity.length === 0 ? (
        <div className="rounded-lg border border-dashed bg-muted/10 px-4 py-6 text-center">
          <MessageSquare className="mx-auto mb-2 h-6 w-6 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">
            {outsideUser
              ? "No activity yet."
              : "No activity yet. Post a comment or link a file from the Files tab."}
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {activity.map((entry) => {
            if (entry.kind === "event") {
              return (
                <li
                  key={`event-${entry.event.id}`}
                  className="rounded-lg border border-dashed bg-background p-3"
                >
                  <div className="mb-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <History className="h-3.5 w-3.5" />
                      <span className="font-medium text-foreground/80">{entry.event.actor_name}</span>
                    </div>
                    <time dateTime={entry.at}>{formatRelativeTime(entry.at)}</time>
                  </div>
                  <p className="text-sm break-words">{formatGrantOpportunityEventSummary(entry.event)}</p>
                </li>
              );
            }

            if (entry.kind === "document") {
              return (
                <li
                  key={`document-${entry.link.id}`}
                  className="rounded-lg border bg-muted/10 p-3"
                >
                  <div className="mb-1.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <FileText className="h-3.5 w-3.5" />
                      <span className="font-medium text-foreground/80">{entry.link.linked_by_name}</span>
                      <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-normal">
                        {GRANT_OPPORTUNITY_DOCUMENT_ROLE_LABELS[entry.link.role]}
                      </Badge>
                    </div>
                    <time dateTime={entry.at}>{formatRelativeTime(entry.at)}</time>
                  </div>
                  <p className="text-sm font-medium break-words [overflow-wrap:anywhere]">
                    {entry.link.label ?? entry.link.name}
                  </p>
                  {entry.link.file_path ? (
                    <p className="mt-1 text-xs text-muted-foreground break-words [overflow-wrap:anywhere]">
                      {entry.link.file_path}
                    </p>
                  ) : null}
                </li>
              );
            }

            if (entry.kind === "document_comment") {
              return (
                <li
                  key={`document-comment-${entry.comment.id}`}
                  className="rounded-lg border bg-muted/20 p-3"
                >
                  <div className="mb-1.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <FileText className="h-3.5 w-3.5 shrink-0" />
                      <span className="font-medium text-foreground/80">{entry.comment.author_name}</span>
                      <Badge variant="secondary" className="h-5 max-w-full px-1.5 text-[10px] font-normal">
                        <span className="truncate">
                          {entry.comment.document_label ?? entry.comment.document_name}
                        </span>
                      </Badge>
                    </div>
                    <time dateTime={entry.at}>{formatRelativeTime(entry.at)}</time>
                  </div>
                  <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{entry.comment.body}</p>
                  {entry.replies.length > 0 ? (
                    <ul className="mt-3 space-y-2 border-l-2 border-border/60 pl-3">
                      {entry.replies.map((reply) => (
                        <li key={reply.id} className="rounded-md bg-background/80 p-2.5">
                          <div className="mb-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                            <span className="font-medium text-foreground/80">{reply.author_name}</span>
                            <time dateTime={reply.created_at}>
                              {formatRelativeTime(reply.created_at)}
                            </time>
                          </div>
                          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{reply.body}</p>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              );
            }

            return (
              <li key={`comment-${entry.comment.id}`} className="rounded-lg border bg-muted/20 p-3">
                <div className="mb-1.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground/80">{entry.comment.author_name}</span>
                  <time dateTime={entry.at}>{formatRelativeTime(entry.at)}</time>
                </div>
                <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{entry.comment.body}</p>
                {!outsideUser && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-2 h-7 px-2 text-xs text-muted-foreground"
                    onClick={() => setReplyingTo(entry.comment.id)}
                  >
                    <Reply className="mr-1.5 h-3 w-3" />
                    Reply
                  </Button>
                )}
                {entry.replies.length > 0 ? (
                  <ul className="mt-3 space-y-2 border-l-2 border-border/60 pl-3">
                    {entry.replies.map((reply) => (
                      <li key={reply.id} className="rounded-md bg-background/80 p-2.5">
                        <div className="mb-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                          <span className="font-medium text-foreground/80">{reply.author_name}</span>
                          <time dateTime={reply.created_at}>
                            {formatRelativeTime(reply.created_at)}
                          </time>
                        </div>
                        <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{reply.body}</p>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {replyingTo === entry.comment.id ? (
                  <div className="mt-3 space-y-2 rounded-lg border bg-background p-2">
                    <Textarea
                      value={replyDraft}
                      onChange={(event) => setReplyDraft(event.target.value)}
                      rows={2}
                      placeholder="Write a reply…"
                      aria-label="Reply to comment"
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setReplyingTo(null);
                          setReplyDraft("");
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        disabled={!replyDraft.trim() || createMutation.isPending}
                        onClick={() =>
                          createMutation.mutate({
                            body: replyDraft.trim(),
                            parentId: entry.comment.id,
                          })
                        }
                      >
                        {createMutation.isPending ? "Posting…" : "Post reply"}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {!outsideUser && (
        <div className="space-y-2 rounded-lg border bg-background p-3">
          <Textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            rows={3}
            placeholder="Leave a note for your team…"
            aria-label="New activity comment"
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              disabled={!draft.trim() || createMutation.isPending}
              onClick={() => createMutation.mutate({ body: draft.trim() })}
            >
              {createMutation.isPending ? "Posting…" : "Post comment"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
